import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Plus, Trash2, Edit2, Save, RefreshCw, Download, Search, X, Check, Network, ArrowRight, Sparkles, Loader2,
} from 'lucide-react';
import {
  docProcessingApi, ConceptNode, ConceptEdge, ConceptGraphStats,
  CandidateNode, CandidateEdge,
} from '../../services/docProcessingApi';
import ConceptGraphVisualizer from './ConceptGraphVisualizer';

const NODE_TYPES = [
  'process', 'document', 'equipment', 'unit', 'transport', 'operation',
  'charge', 'compliance', 'pricing', 'party', 'service', 'schedule',
  'deadline', 'policy', 'system', 'status', 'entity', 'concept', 'cargo_type',
];

const EMPTY_NODE: ConceptNode = {
  id: '', name: '', node_type: 'process', description: '', aliases: [], module: '',
};

export default function ConceptGraphEditor() {
  const [nodes, setNodes] = useState<ConceptNode[]>([]);
  const [edges, setEdges] = useState<ConceptEdge[]>([]);
  const [stats, setStats] = useState<ConceptGraphStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'nodes' | 'edges' | 'graph'>('nodes');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');

  // Edit/Add state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ConceptNode>(EMPTY_NODE);
  const [showAddNode, setShowAddNode] = useState(false);
  const [newNode, setNewNode] = useState<ConceptNode>({ ...EMPTY_NODE });
  const [aliasInput, setAliasInput] = useState('');
  const [newAliasInput, setNewAliasInput] = useState('');

  // Edge add state
  const [showAddEdge, setShowAddEdge] = useState(false);
  const [newEdge, setNewEdge] = useState({ from: '', to: '', relation: '' });

  // Confirm delete
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Generate from documents state
  const [generating, setGenerating] = useState(false);
  // Batch generation state
  const [batchJobId, setBatchJobId] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ total: number; completed: number; failed: number; status: string } | null>(null);
  const [candidateNodes, setCandidateNodes] = useState<CandidateNode[]>([]);
  const [candidateEdges, setCandidateEdges] = useState<CandidateEdge[]>([]);
  const [showReview, setShowReview] = useState(false);
  const [selectedCandidateNodes, setSelectedCandidateNodes] = useState<Set<string>>(new Set());
  const [selectedCandidateEdges, setSelectedCandidateEdges] = useState<Set<string>>(new Set());
  const [generateSummary, setGenerateSummary] = useState<any>(null);

  const clearMessages = () => { setError(null); setSuccess(null); };
  const showSuccess = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();
      const [nodesRes, edgesRes, statsRes] = await Promise.all([
        docProcessingApi.listConceptNodes(),
        docProcessingApi.listConceptEdges(),
        docProcessingApi.getConceptGraphStats(),
      ]);
      setNodes(nodesRes.nodes || []);
      setEdges(edgesRes.edges || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message || 'Failed to load knowledge graph');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Derived filter options
  const uniqueTypes = useMemo(() => Array.from(new Set(nodes.map(n => n.node_type))).sort(), [nodes]);
  const uniqueModules = useMemo(() => Array.from(new Set(nodes.map(n => n.module).filter(Boolean))).sort(), [nodes]);

  // Filtered nodes
  const filteredNodes = useMemo(() => {
    let filtered = nodes;
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(n =>
        n.id.toLowerCase().includes(lower) ||
        n.name.toLowerCase().includes(lower) ||
        n.description.toLowerCase().includes(lower) ||
        n.aliases.some(a => a.toLowerCase().includes(lower))
      );
    }
    if (typeFilter) filtered = filtered.filter(n => n.node_type === typeFilter);
    if (moduleFilter) filtered = filtered.filter(n => n.module === moduleFilter);
    return filtered;
  }, [nodes, searchTerm, typeFilter, moduleFilter]);

  // Filtered edges
  const filteredEdges = useMemo(() => {
    if (!searchTerm) return edges;
    const lower = searchTerm.toLowerCase();
    return edges.filter(e =>
      e.from_name.toLowerCase().includes(lower) ||
      e.to_name.toLowerCase().includes(lower) ||
      e.relation.toLowerCase().includes(lower)
    );
  }, [edges, searchTerm]);

  // ── Actions ──

  const handleReload = async () => {
    try {
      clearMessages();
      await docProcessingApi.reloadConceptGraph();
      await loadData();
      showSuccess('Knowledge graph reloaded from disk');
    } catch (e: any) { setError(e.message); }
  };

  const handleSave = async () => {
    try {
      clearMessages();
      await docProcessingApi.saveConceptGraph();
      showSuccess('Knowledge graph saved to disk');
    } catch (e: any) { setError(e.message); }
  };

  const handleExport = async () => {
    try {
      const data = await docProcessingApi.exportConceptGraph();
      const blob = new Blob([JSON.stringify(data.graph || data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'concept_graph.json'; a.click();
      URL.revokeObjectURL(url);
      showSuccess('Exported concept_graph.json');
    } catch (e: any) { setError(e.message); }
  };

  // ── Node CRUD ──

  const handleCreateNode = async () => {
    try {
      clearMessages();
      const node = { ...newNode, aliases: newAliasInput ? newAliasInput.split(',').map(s => s.trim()).filter(Boolean) : [] };
      if (!node.id || !node.name) { setError('ID and Name are required'); return; }
      await docProcessingApi.createConceptNode(node);
      setShowAddNode(false);
      setNewNode({ ...EMPTY_NODE });
      setNewAliasInput('');
      await loadData();
      showSuccess(`Node "${node.name}" created`);
    } catch (e: any) { setError(e.message); }
  };

  const handleStartEdit = (node: ConceptNode) => {
    setEditingId(node.id);
    setEditForm({ ...node });
    setAliasInput(node.aliases.join(', '));
  };

  const handleSaveEdit = async () => {
    try {
      clearMessages();
      const update = {
        ...editForm,
        aliases: aliasInput ? aliasInput.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      await docProcessingApi.updateConceptNode(editForm.id, update);
      setEditingId(null);
      await loadData();
      showSuccess(`Node "${editForm.name}" updated`);
    } catch (e: any) { setError(e.message); }
  };

  const handleDeleteNode = async (id: string) => {
    try {
      clearMessages();
      await docProcessingApi.deleteConceptNode(id);
      setDeletingId(null);
      await loadData();
      showSuccess(`Node "${id}" deleted`);
    } catch (e: any) { setError(e.message); }
  };

  // ── Edge CRUD ──

  const handleCreateEdge = async () => {
    try {
      clearMessages();
      if (!newEdge.from || !newEdge.to || !newEdge.relation) {
        setError('From, To, and Relation are required'); return;
      }
      await docProcessingApi.createConceptEdge(newEdge);
      setShowAddEdge(false);
      setNewEdge({ from: '', to: '', relation: '' });
      await loadData();
      showSuccess('Edge created');
    } catch (e: any) { setError(e.message); }
  };

  const handleDeleteEdge = async (from: string, to: string, relation: string) => {
    try {
      clearMessages();
      await docProcessingApi.deleteConceptEdge(from, to, relation);
      await loadData();
      showSuccess('Edge deleted');
    } catch (e: any) { setError(e.message); }
  };

  // ── Auto-slugify ID from name ──
  // ── Generate from Documents ──

  // ── Batch Generate (ALL documents) ──

  const handleBatchGenerate = async () => {
    try {
      clearMessages();
      const result = await docProcessingApi.generateConceptGraphBatch(true, 3);
      setBatchJobId(result.job_id);
      setBatchProgress({ total: 0, completed: 0, failed: 0, status: 'queued' });
      showSuccess('Batch generation started — processing all documents');
    } catch (e: any) {
      setError(e.message);
    }
  };

  // Poll batch job progress
  useEffect(() => {
    if (!batchJobId) return;
    const interval = setInterval(async () => {
      try {
        const status = await docProcessingApi.getJobStatus(batchJobId);
        setBatchProgress({ total: status.total, completed: status.completed, failed: status.failed, status: status.status });
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          setBatchJobId(null);
          if (status.status === 'completed') {
            // Parse results from errors field (reused as metadata)
            const meta = status.errors[0] || '';
            showSuccess(`Batch complete: ${meta}`);
          } else {
            setError(`Batch failed: ${status.errors.join(', ')}`);
          }
          await loadData(); // Refresh graph data
        }
      } catch (e: any) {
        console.warn('Job poll failed:', e.message);
      }
    }, 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [batchJobId, loadData]);

  // Track active quick-sample poll interval for cleanup
  const quickPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (quickPollRef.current) clearInterval(quickPollRef.current);
    };
  }, []);

  const handleGenerate = async () => {
    try {
      clearMessages();
      setGenerating(true);
      // Quick sample now runs as background job — start it and poll
      const result = await docProcessingApi.generateConceptGraph(50);
      const jobId = result.job_id;
      if (!jobId) {
        setError('No job_id returned'); setGenerating(false); return;
      }
      // Poll until complete
      if (quickPollRef.current) clearInterval(quickPollRef.current);
      quickPollRef.current = setInterval(async () => {
        try {
          const status = await docProcessingApi.getJobStatus(jobId);
          if (status.status === 'completed') {
            if (quickPollRef.current) clearInterval(quickPollRef.current);
            quickPollRef.current = null;
            setGenerating(false);
            // Results are stored in errors[0] as JSON
            try {
              const data = JSON.parse(status.errors[0] || '{}');
              if (data.candidates) {
                setCandidateNodes(data.candidates.nodes || []);
                setCandidateEdges(data.candidates.edges || []);
                setGenerateSummary(data.summary);
                const newNodeIds: Set<string> = new Set((data.candidates.nodes || []).filter((n: any) => n.is_new).map((n: any) => n.id as string));
                const newEdgeKeys: Set<string> = new Set((data.candidates.edges || []).filter((e: any) => e.is_new).map((e: any) => `${e.from}->${e.to}:${e.relation}` as string));
                setSelectedCandidateNodes(newNodeIds);
                setSelectedCandidateEdges(newEdgeKeys);
                setShowReview(true);
                showSuccess(`Found ${data.summary?.new_nodes || 0} new concepts and ${data.summary?.new_edges || 0} new relationships`);
              }
            } catch { showSuccess('Quick sample complete — check results'); }
          } else if (status.status === 'failed') {
            if (quickPollRef.current) clearInterval(quickPollRef.current);
            quickPollRef.current = null;
            setGenerating(false);
            setError(`Quick sample failed: ${status.errors.join(', ')}`);
          }
        } catch { /* ignore poll errors */ }
      }, 2000);
    } catch (e: any) {
      setError(e.message);
      setGenerating(false);
    }
  };

  const handleAcceptSelected = async () => {
    try {
      clearMessages();
      let addedNodes = 0;
      let addedEdges = 0;

      // Add selected nodes first (edges depend on nodes existing)
      for (const node of candidateNodes) {
        if (!selectedCandidateNodes.has(node.id)) continue;
        if (!node.is_new) continue;
        try {
          await docProcessingApi.createConceptNode({
            id: node.id, name: node.name, node_type: node.node_type,
            description: node.description, aliases: node.aliases, module: node.module,
          });
          addedNodes++;
        } catch (e: any) {
          tracing: console.warn(`Failed to add node ${node.id}: ${e.message}`);
        }
      }

      // Add selected edges
      for (const edge of candidateEdges) {
        const key = `${edge.from}->${edge.to}:${edge.relation}`;
        if (!selectedCandidateEdges.has(key)) continue;
        if (!edge.is_new) continue;
        try {
          await docProcessingApi.createConceptEdge({ from: edge.from, to: edge.to, relation: edge.relation });
          addedEdges++;
        } catch (e: any) {
          console.warn(`Failed to add edge ${edge.from}->${edge.to}: ${e.message}`);
        }
      }

      setShowReview(false);
      setCandidateNodes([]);
      setCandidateEdges([]);
      await loadData();
      showSuccess(`Added ${addedNodes} nodes and ${addedEdges} edges to the graph`);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleCandidateNode = (id: string) => {
    setSelectedCandidateNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleCandidateEdge = (key: string) => {
    setSelectedCandidateEdges(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ── Auto-slugify ID from name ──
  const slugify = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Loading knowledge graph...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Stats + Actions bar */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-3">
        <div className="flex items-center gap-4">
          <Network size={20} className="text-blue-600" />
          <span className="text-sm text-gray-600">
            <strong>{stats?.node_count ?? nodes.length}</strong> nodes
            <span className="mx-1.5 text-gray-300">·</span>
            <strong>{stats?.edge_count ?? edges.length}</strong> edges
            <span className="mx-1.5 text-gray-300">·</span>
            <strong>{stats?.alias_count ?? 0}</strong> aliases
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReload} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
            <RefreshCw size={13} /> Reload
          </button>
          <button onClick={handleSave} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
            <Save size={13} /> Save
          </button>
          <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
            <Download size={13} /> Export
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || !!batchJobId}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg"
            title="Quick sample: extract from 50 random chunks"
          >
            {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {generating ? 'Sampling...' : 'Quick Sample'}
          </button>
          <button
            onClick={handleBatchGenerate}
            disabled={generating || !!batchJobId}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg"
            title="Generate Knowledge Graph nodes/edges for documents not yet processed (runs in background)"
          >
            {batchJobId ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {batchJobId ? 'Running...' : 'Generate KG for Missing Docs'}
          </button>
        </div>
      </div>

      {/* Batch progress bar */}
      {batchProgress && batchProgress.status !== 'completed' && batchProgress.status !== 'failed' && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-indigo-800 flex items-center gap-1.5">
              <Loader2 size={13} className="animate-spin" />
              Generating Knowledge Graph for missing documents...
            </span>
            <span className="text-xs text-indigo-600">
              {batchProgress.completed} / {batchProgress.total} docs
              {batchProgress.failed > 0 && <span className="text-red-500 ml-1">({batchProgress.failed} failed)</span>}
            </span>
          </div>
          <div className="w-full bg-indigo-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
              style={{ width: batchProgress.total > 0 ? `${(batchProgress.completed / batchProgress.total * 100).toFixed(0)}%` : '0%' }}
            />
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg text-sm flex items-center justify-between">
          {success}
          <button onClick={() => setSuccess(null)}><X size={14} /></button>
        </div>
      )}

      {/* ── REVIEW PANEL (generated candidates) ── */}
      {showReview && (
        <div className="bg-white rounded-xl shadow-sm border border-purple-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-purple-800 flex items-center gap-1.5">
                <Sparkles size={14} /> Review Generated Concepts
              </h3>
              {generateSummary && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Sampled {generateSummary.chunks_sampled} chunks — found {generateSummary.new_nodes} new concepts, {generateSummary.new_edges} new relationships
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={handleAcceptSelected} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg">
                <Check size={13} /> Accept Selected ({selectedCandidateNodes.size + selectedCandidateEdges.size})
              </button>
              <button onClick={() => setShowReview(false)} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
                Cancel
              </button>
            </div>
          </div>

          {/* Candidate Nodes */}
          {candidateNodes.filter(n => n.is_new).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase mb-1">
                New Concepts ({candidateNodes.filter(n => n.is_new).length})
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 font-semibold uppercase">
                      <th className="text-left px-3 py-1.5 w-8"></th>
                      <th className="text-left px-3 py-1.5">Name</th>
                      <th className="text-left px-3 py-1.5">Type</th>
                      <th className="text-left px-3 py-1.5">Module</th>
                      <th className="text-left px-3 py-1.5">Aliases</th>
                      <th className="text-left px-3 py-1.5">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {candidateNodes.filter(n => n.is_new).map(node => (
                      <tr key={node.id} className={`hover:bg-purple-50 ${selectedCandidateNodes.has(node.id) ? 'bg-purple-50' : ''}`}>
                        <td className="px-3 py-1.5">
                          <input
                            type="checkbox"
                            checked={selectedCandidateNodes.has(node.id)}
                            onChange={() => toggleCandidateNode(node.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-3 py-1.5 font-medium text-gray-900">{node.name}</td>
                        <td className="px-3 py-1.5"><span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{node.node_type}</span></td>
                        <td className="px-3 py-1.5 text-gray-600">{node.module}</td>
                        <td className="px-3 py-1.5 text-gray-500">{node.aliases.join(', ')}</td>
                        <td className="px-3 py-1.5 text-gray-500 max-w-xs truncate">{node.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Candidate Edges */}
          {candidateEdges.filter(e => e.is_new).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600 uppercase mb-1">
                New Relationships ({candidateEdges.filter(e => e.is_new).length})
              </div>
              <div className="max-h-36 overflow-y-auto border border-gray-100 rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 font-semibold uppercase">
                      <th className="text-left px-3 py-1.5 w-8"></th>
                      <th className="text-left px-3 py-1.5">From</th>
                      <th className="text-center px-2 py-1.5">Relation</th>
                      <th className="text-left px-3 py-1.5">To</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {candidateEdges.filter(e => e.is_new).map((edge, i) => {
                      const key = `${edge.from}->${edge.to}:${edge.relation}`;
                      return (
                        <tr key={`${key}-${i}`} className={`hover:bg-purple-50 ${selectedCandidateEdges.has(key) ? 'bg-purple-50' : ''}`}>
                          <td className="px-3 py-1.5">
                            <input
                              type="checkbox"
                              checked={selectedCandidateEdges.has(key)}
                              onChange={() => toggleCandidateEdge(key)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-3 py-1.5 font-medium text-gray-900">{edge.from}</td>
                          <td className="px-2 py-1.5 text-center"><span className="px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600">{edge.relation}</span></td>
                          <td className="px-3 py-1.5 font-medium text-gray-900">{edge.to}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {candidateNodes.filter(n => n.is_new).length === 0 && candidateEdges.filter(e => e.is_new).length === 0 && (
            <div className="text-center text-sm text-gray-400 py-4">
              No new concepts found — the existing graph already covers these documents.
            </div>
          )}
        </div>
      )}

      {/* Panel toggle + search */}
      <div className="flex items-center gap-3">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setActivePanel('nodes')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activePanel === 'nodes' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Nodes ({nodes.length})
          </button>
          <button
            onClick={() => setActivePanel('edges')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activePanel === 'edges' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Edges ({edges.length})
          </button>
          <button
            onClick={() => setActivePanel('graph')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activePanel === 'graph' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <span className="flex items-center gap-1"><Network size={13} /> Graph</span>
          </button>
        </div>

        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>

        {activePanel === 'nodes' && (
          <>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none">
              <option value="">All Types</option>
              {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none">
              <option value="">All Modules</option>
              {uniqueModules.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <button onClick={() => { setShowAddNode(true); setNewNode({ ...EMPTY_NODE }); setNewAliasInput(''); }} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
              <Plus size={14} /> Add Node
            </button>
          </>
        )}

        {activePanel === 'edges' && (
          <button onClick={() => { setShowAddEdge(true); setNewEdge({ from: '', to: '', relation: '' }); }} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
            <Plus size={14} /> Add Edge
          </button>
        )}
      </div>

      {/* ── NODES TABLE ── */}
      {activePanel === 'nodes' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Add node form */}
          {showAddNode && (
            <div className="border-b border-blue-100 bg-blue-50 px-4 py-3 space-y-2">
              <div className="grid grid-cols-6 gap-2">
                <input placeholder="ID" value={newNode.id} onChange={e => setNewNode({ ...newNode, id: e.target.value })}
                  className="col-span-1 text-sm border rounded-lg px-2 py-1.5" />
                <input placeholder="Name" value={newNode.name}
                  onChange={e => {
                    const name = e.target.value;
                    setNewNode({ ...newNode, name, id: newNode.id || slugify(name) });
                  }}
                  className="col-span-1 text-sm border rounded-lg px-2 py-1.5" />
                <select value={newNode.node_type} onChange={e => setNewNode({ ...newNode, node_type: e.target.value })}
                  className="col-span-1 text-sm border rounded-lg px-2 py-1.5">
                  {NODE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input placeholder="Module" value={newNode.module} onChange={e => setNewNode({ ...newNode, module: e.target.value })}
                  className="col-span-1 text-sm border rounded-lg px-2 py-1.5" />
                <input placeholder="Aliases (comma-separated)" value={newAliasInput} onChange={e => setNewAliasInput(e.target.value)}
                  className="col-span-2 text-sm border rounded-lg px-2 py-1.5" />
              </div>
              <div className="flex gap-2">
                <input placeholder="Description" value={newNode.description} onChange={e => setNewNode({ ...newNode, description: e.target.value })}
                  className="flex-1 text-sm border rounded-lg px-2 py-1.5" />
                <button onClick={handleCreateNode} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
                  <Check size={14} /> Create
                </button>
                <button onClick={() => setShowAddNode(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          )}

          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs font-semibold uppercase">
                <th className="text-left px-4 py-2.5">ID</th>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Module</th>
                <th className="text-left px-4 py-2.5">Aliases</th>
                <th className="text-left px-4 py-2.5">Description</th>
                <th className="text-right px-4 py-2.5 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredNodes.map(node => (
                editingId === node.id ? (
                  <tr key={node.id} className="bg-yellow-50">
                    <td className="px-4 py-2 text-gray-400 font-mono text-xs">{node.id}</td>
                    <td className="px-4 py-2">
                      <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full text-sm border rounded px-2 py-1" />
                    </td>
                    <td className="px-4 py-2">
                      <select value={editForm.node_type} onChange={e => setEditForm({ ...editForm, node_type: e.target.value })} className="text-sm border rounded px-1 py-1">
                        {NODE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input value={editForm.module} onChange={e => setEditForm({ ...editForm, module: e.target.value })} className="w-full text-sm border rounded px-2 py-1" />
                    </td>
                    <td className="px-4 py-2">
                      <input value={aliasInput} onChange={e => setAliasInput(e.target.value)} placeholder="comma-separated" className="w-full text-sm border rounded px-2 py-1" />
                    </td>
                    <td className="px-4 py-2">
                      <input value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="w-full text-sm border rounded px-2 py-1" />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={handleSaveEdit} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={15} /></button>
                      <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X size={15} /></button>
                    </td>
                  </tr>
                ) : (
                  <tr key={node.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{node.id}</td>
                    <td className="px-4 py-2 font-medium text-gray-900">{node.name}</td>
                    <td className="px-4 py-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{node.node_type}</span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{node.module}</td>
                    <td className="px-4 py-2">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600" title={node.aliases.join(', ')}>
                        {node.aliases.length} alias{node.aliases.length !== 1 ? 'es' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs max-w-xs truncate">{node.description}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => handleStartEdit(node)} className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={14} /></button>
                      {deletingId === node.id ? (
                        <>
                          <button onClick={() => handleDeleteNode(node.id)} className="p-1 text-red-600 hover:bg-red-50 rounded text-xs font-medium">Yes</button>
                          <button onClick={() => setDeletingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded text-xs">No</button>
                        </>
                      ) : (
                        <button onClick={() => setDeletingId(node.id)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                      )}
                    </td>
                  </tr>
                )
              ))}
              {filteredNodes.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No nodes found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── EDGES TABLE ── */}
      {activePanel === 'edges' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {showAddEdge && (
            <div className="border-b border-blue-100 bg-blue-50 px-4 py-3 flex items-center gap-2">
              <select value={newEdge.from} onChange={e => setNewEdge({ ...newEdge, from: e.target.value })}
                className="text-sm border rounded-lg px-2 py-1.5 flex-1">
                <option value="">From node...</option>
                {nodes.map(n => <option key={n.id} value={n.id}>{n.name} ({n.id})</option>)}
              </select>
              <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />
              <input placeholder="Relation (e.g. produces, requires)" value={newEdge.relation}
                onChange={e => setNewEdge({ ...newEdge, relation: e.target.value })}
                className="text-sm border rounded-lg px-2 py-1.5 flex-1" />
              <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />
              <select value={newEdge.to} onChange={e => setNewEdge({ ...newEdge, to: e.target.value })}
                className="text-sm border rounded-lg px-2 py-1.5 flex-1">
                <option value="">To node...</option>
                {nodes.map(n => <option key={n.id} value={n.id}>{n.name} ({n.id})</option>)}
              </select>
              <button onClick={handleCreateEdge} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
                <Check size={14} /> Add
              </button>
              <button onClick={() => setShowAddEdge(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
            </div>
          )}

          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs font-semibold uppercase">
                <th className="text-left px-4 py-2.5">From</th>
                <th className="text-center px-2 py-2.5 w-10"></th>
                <th className="text-left px-4 py-2.5">Relation</th>
                <th className="text-center px-2 py-2.5 w-10"></th>
                <th className="text-left px-4 py-2.5">To</th>
                <th className="text-right px-4 py-2.5 w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredEdges.map((edge, i) => (
                <tr key={`${edge.from}-${edge.to}-${edge.relation}-${i}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{edge.from_name}</td>
                  <td className="text-center text-gray-300"><ArrowRight size={14} /></td>
                  <td className="px-4 py-2">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-600">{edge.relation}</span>
                  </td>
                  <td className="text-center text-gray-300"><ArrowRight size={14} /></td>
                  <td className="px-4 py-2 font-medium text-gray-900">{edge.to_name}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => handleDeleteEdge(edge.from, edge.to, edge.relation)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {filteredEdges.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No edges found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── GRAPH VISUALIZATION ── */}
      {activePanel === 'graph' && (() => {
        // When search/filters are active, show only matching nodes + their edges
        const graphNodes = (searchTerm || typeFilter || moduleFilter) ? filteredNodes : nodes;
        const graphNodeIds = new Set(graphNodes.map(n => n.id));
        const graphEdges = edges.filter(e => graphNodeIds.has(e.from) && graphNodeIds.has(e.to));
        return <ConceptGraphVisualizer nodes={graphNodes} edges={graphEdges} />;
      })()}
    </div>
  );
}
